class VaillantDrawerNav {
  constructor(drawerElement) {
    this.drawer = drawerElement;
    this.viewport = drawerElement.querySelector('[data-vaillant-drawer-viewport]');
    this.panels = drawerElement.querySelectorAll('.vaillant-drawer__panel');
    this.rootPanel = drawerElement.querySelector('[data-panel="root"]');
    this.mainDetails = drawerElement.closest('details');

    if (!this.viewport || !this.panels.length) return;

    this.bindEvents();
  }

  bindEvents() {
    this.drawer.querySelectorAll('[data-navigate]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.navigateTo(trigger.dataset.navigate);
      });
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          this.navigateTo(trigger.dataset.navigate);
        }
      });
    });

    this.drawer.querySelectorAll('.vaillant-drawer__expand-trigger').forEach((trigger) => {
      const toggle = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleLevel2(trigger.closest('[data-level2-item]'));
      };
      trigger.addEventListener('click', toggle);
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') toggle(event);
      });
    });

    this.mainDetails?.addEventListener('toggle', () => {
      if (!this.mainDetails.open) this.reset();
    });

    this.drawer.querySelector('[data-vaillant-drawer-close]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.closeDrawer();
    });

    this.bindSwipeBack();
  }

  closeDrawer() {
    const headerDrawer = this.drawer.closest('header-drawer');
    const summary = this.mainDetails?.querySelector('summary');

    if (headerDrawer && typeof headerDrawer.closeMenuDrawer === 'function') {
      headerDrawer.closeMenuDrawer(new Event('click'), summary);
      return;
    }

    this.mainDetails?.removeAttribute('open');
    summary?.setAttribute('aria-expanded', 'false');
    document.querySelector('.section-header')?.classList.remove('menu-open');
  }

  bindSwipeBack() {
    let touchStartX = 0;

    this.viewport?.addEventListener(
      'touchstart',
      (event) => {
        touchStartX = event.changedTouches[0].screenX;
      },
      { passive: true }
    );

    this.viewport?.addEventListener(
      'touchend',
      (event) => {
        const touchEndX = event.changedTouches[0].screenX;
        const activePanel = this.drawer.querySelector('.vaillant-drawer__panel.is-active');
        if (!activePanel || activePanel.dataset.panel === 'root') return;

        if (touchEndX - touchStartX > 60) {
          const parentId = activePanel.dataset.panelParent;
          if (parentId) this.navigateTo(parentId);
        }
      },
      { passive: true }
    );
  }

  navigateTo(panelId) {
    const targetPanel = this.drawer.querySelector(`[data-panel="${panelId}"]`);
    if (!targetPanel) return;

    const parentId = targetPanel.dataset.panelParent;

    this.panels.forEach((panel) => {
      panel.classList.remove('is-active', 'is-previous');
      panel.hidden = true;
    });

    if (parentId) {
      const parentPanel = this.drawer.querySelector(`[data-panel="${parentId}"]`);
      if (parentPanel) {
        parentPanel.hidden = false;
        parentPanel.classList.add('is-previous');
      }
    }

    targetPanel.hidden = false;
    targetPanel.classList.add('is-active');
    this.collapseAllLevel2(targetPanel);
  }

  toggleLevel2(item) {
    if (!item) return;
    if (item.classList.contains('is-expanded')) {
      this.collapseItem(item);
      return;
    }
    this.expandItem(item);
  }

  expandItem(item) {
    if (!item) return;

    item
      .closest('.vaillant-drawer__panel')
      ?.querySelectorAll('[data-level2-item].is-expanded')
      .forEach((expandedItem) => {
        if (expandedItem !== item) this.collapseItem(expandedItem);
      });

    item.classList.add('is-expanded');
    item.querySelector('.vaillant-drawer__list--level3')?.removeAttribute('hidden');
    item.querySelector('.vaillant-drawer__expand-trigger')?.setAttribute('aria-expanded', 'true');
  }

  collapseItem(item) {
    if (!item) return;

    const trigger = item.querySelector('.vaillant-drawer__expand-trigger');

    item.classList.remove('is-expanded');
    item.querySelector('.vaillant-drawer__list--level3')?.setAttribute('hidden', '');
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.blur();
  }

  collapseAllLevel2(panel = null) {
    const scope = panel || this.drawer;
    scope.querySelectorAll('[data-level2-item].is-expanded').forEach((item) => {
      this.collapseItem(item);
    });
  }

  reset() {
    this.panels.forEach((panel) => {
      panel.classList.remove('is-active', 'is-previous');
      panel.hidden = panel.dataset.panel !== 'root';
    });

    if (this.rootPanel) {
      this.rootPanel.hidden = false;
      this.rootPanel.classList.add('is-active');
    }

    this.collapseAllLevel2();
    this.drawer.querySelectorAll('localization-form').forEach((form) => {
      if (typeof form.hidePanel === 'function') form.hidePanel();
    });
    this.drawer.classList.remove('vaillant-drawer--localization-open');
  }
}

function patchMenuDrawerClass(MenuDrawer) {
  if (!MenuDrawer || MenuDrawer.prototype._vaillantMenuPatched) return;
  MenuDrawer.prototype._vaillantMenuPatched = true;

  const menuProto = MenuDrawer.prototype;
  const originalOnSummaryClick = menuProto.onSummaryClick;
  const originalOnFocusOut = menuProto.onFocusOut;
  const originalOnCloseButtonClick = menuProto.onCloseButtonClick;
  const originalCloseMenuDrawer = menuProto.closeMenuDrawer;

  menuProto.onCloseButtonClick = function (event) {
    if (event.currentTarget.closest('.vaillant-drawer')) return;
    originalOnCloseButtonClick.call(this, event);
  };

  menuProto.onFocusOut = function () {
    if (this.querySelector('.vaillant-drawer')) return;
    originalOnFocusOut.call(this);
  };

  menuProto.closeMenuDrawer = function (event, elementToFocus = false) {
    if (this.querySelector('.vaillant-drawer') && event === undefined) return;
    originalCloseMenuDrawer.call(this, event, elementToFocus);
  };

  menuProto.onSummaryClick = function (event) {
    if (!this.querySelector('.vaillant-drawer')) {
      originalOnSummaryClick.call(this, event);
      return;
    }

    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;

    if (detailsElement !== this.mainDetailsToggle) {
      originalOnSummaryClick.call(this, event);
      return;
    }

    event.preventDefault();

    const isOpen = detailsElement.hasAttribute('open');

    if (isOpen) {
      this.closeMenuDrawer(event, summaryElement);
    } else {
      detailsElement.setAttribute('open', '');
      this.openMenuDrawer(summaryElement);
    }

    if (window.matchMedia('(max-width: 990px)').matches) {
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    }
  };
}

function patchHeaderDrawerClass(HeaderDrawer) {
  if (!HeaderDrawer || HeaderDrawer.prototype._vaillantHeaderPatched) return;
  HeaderDrawer.prototype._vaillantHeaderPatched = true;

  const headerProto = HeaderDrawer.prototype;
  const originalHeaderOpen = headerProto.openMenuDrawer;
  const originalHeaderClose = headerProto.closeMenuDrawer;

  headerProto.openMenuDrawer = function (summaryElement) {
    if (!this.querySelector('.vaillant-drawer')) {
      originalHeaderOpen.call(this, summaryElement);
      return;
    }

    originalHeaderOpen.call(this, summaryElement);

    requestAnimationFrame(() => {
      if (typeof this.onResize === 'function') this.onResize();
    });
  };

  headerProto.closeMenuDrawer = function (event, elementToFocus) {
    if (!this.querySelector('.vaillant-drawer')) {
      originalHeaderClose.call(this, event, elementToFocus);
      return;
    }

    if (event === undefined) return;

    const detailsElement = this.mainDetailsToggle;
    const summaryElement = elementToFocus || detailsElement.querySelector('summary');

    originalHeaderClose.call(this, event, summaryElement);

    this.header?.classList.add('menu-open');

    detailsElement.addEventListener(
      'toggle',
      () => {
        if (detailsElement.open) return;

        this.header?.classList.remove('menu-open');
        summaryElement?.setAttribute('aria-expanded', 'false');
      },
      { once: true }
    );
  };
}

const originalDefine = customElements.define.bind(customElements);

customElements.define = function (name, constructor, options) {
  if (name === 'menu-drawer') patchMenuDrawerClass(constructor);
  if (name === 'header-drawer') patchHeaderDrawerClass(constructor);

  return originalDefine(name, constructor, options);
};

function initVaillantDrawer() {
  document.querySelectorAll('.vaillant-drawer:not([data-vaillant-init])').forEach((drawer) => {
    drawer.setAttribute('data-vaillant-init', '');
    new VaillantDrawerNav(drawer);
  });
}

document.addEventListener('DOMContentLoaded', initVaillantDrawer);
